/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import styled from 'styled-components';
import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import { useLocation } from 'react-router';

type Props = { tagName: string } & DetailedHTMLProps<HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>;

const DynamicTag: React.FC<Props> = (props) => {
  const { tagName, children, ...restProps } = props;
  return React.createElement(tagName || 'div', restProps, children);
};

const H = styled(DynamicTag)`
  .anchor-link {
    color: #666;
    opacity: 0;
    margin-left: 3px;
  }
  &:hover {
    .anchor-link {
      opacity: 1;
      transition: opacity linear 0.4ms;
    }
  }
`;

export const TitleAnchorLink: React.FC<Props> = (props) => {
  const { pathname } = useLocation();

  const getLink = (text: string) => {
    const emojiRegex =
      /[\u{1F000}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu;

    return text
      .toLowerCase()
      .replace(emojiRegex, '')
      .trim()
      .replace(/[^a-z0-9]/g, '-');
  };
  const link = getLink((props.children as string) || '');

  const directLink = `#${pathname}#${link}`;
  return (
    <H tagName={props.tagName}>
      {props.children}
      <a
        data-level={props.tagName}
        title={(props.children as string) ?? ''}
        href={directLink}
        id={link}
        className="anchor-link"
      >
        #
      </a>
    </H>
  );
};
